import { memo } from "react";
import { motion } from "framer-motion";
import { Clock, Camera, Printer, FileText, Receipt, DollarSign, TicketX, ChevronLeft, ChevronRight, ShieldCheck } from "lucide-react";

// Sprint 46.0 — Tarjeta de vehículo memoizada del Kanban de Valet.
// `api` es un objeto ESTABLE (identidad fija) cuyo `api.get()` devuelve SIEMPRE los últimos
// handlers/helpers (via ref en el padre) => sin closures obsoletos y sin tocar la lógica.
// memo (shallow) evita redibujar tarjetas cuyo `v`/estado/modo/búsqueda no cambió.
function ValetVehicleCardBase({ v, estado, cobraValet, q, api }) {
  const h = api.get();
  const isSolicitado = estado === "Solicitado";
  const isPorEntregar = estado === "Pagado";
  const sla = isSolicitado ? h.slaFor(v) : null;
  const perm = isPorEntregar ? h.permanenceFor(v) : null;
  const slaBorder = sla
    ? sla.tier === "red"
      ? "border-2 border-[#EF4444] valet-sla-blink"
      : sla.tier === "orange"
      ? "border-2 border-[#F59E0B]"
      : "border-2 border-[#10B981]"
    : perm && perm.over
    ? "border-2 border-[#EF4444] valet-perm-blink"
    : `border border-white/15 ${cobraValet ? (h.esPagado(v) ? "border-l-4 border-l-[#10B981]" : "border-l-4 border-l-[#EF4444]") : ""}`;
  return (
    <motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}
      className={`bg-[#0A0A0A] p-2.5 md:p-3 ${slaBorder}`} data-testid={`valet-card-${v.id_detalle}`}>
      {isSolicitado && (
        <div data-testid={`valet-sla-${v.id_detalle}`} data-sla-tier={sla.tier}
          className={`flex items-center gap-1.5 mb-2 px-2 py-1 leading-none ${
            sla.tier === "red"
              ? "bg-[#EF4444]/20 text-[#EF4444]"
              : sla.tier === "orange"
              ? "bg-[#F59E0B]/15 text-[#F59E0B]"
              : "bg-[#10B981]/10 text-[#10B981]"
          }`}>
          <Clock className={`h-3.5 w-3.5 ${sla.tier === "red" ? "animate-pulse" : ""}`} />
          <span className={`font-mono tabular-nums ${
            sla.tier === "red" ? "text-base font-black" : sla.tier === "orange" ? "text-sm font-bold" : "text-sm font-semibold"
          }`}>{sla.label}</span>
          <span className="text-[9px] font-bold uppercase tracking-wider opacity-70 ml-auto">Espera</span>
        </div>
      )}
      {estado === "Pagado" && perm && (
        <div data-testid={`valet-permanencia-${v.id_detalle}`} data-perm-over={perm.over ? "1" : "0"}
          className={`flex items-center gap-1.5 mb-2 px-2 py-1 leading-none ${perm.over ? "bg-[#EF4444]/20 text-[#EF4444]" : "bg-[#F59E0B]/10 text-[#F59E0B]"}`}>
          <Clock className={`h-3.5 w-3.5 ${perm.over ? "animate-pulse" : ""}`} />
          <span className="text-[9px] font-bold uppercase tracking-wider opacity-70">En bahía</span>
          <span className={`font-mono tabular-nums ml-auto ${perm.over ? "text-base font-black" : "text-sm font-semibold"}`}>{perm.label}</span>
        </div>
      )}
      {estado === "Pagado" && v.tiempo_espera_seg != null && (
        <div data-testid={`valet-sello-${v.id_detalle}`}
          className="flex items-center gap-1.5 mb-2 px-2 py-1 leading-none bg-white/5 border border-white/10 text-white/70">
          <Clock className="h-3.5 w-3.5 text-[#10B981]" />
          <span className="text-[9px] font-bold uppercase tracking-wider opacity-70">Entregado en</span>
          <span className="font-mono tabular-nums text-sm font-bold text-[#10B981] ml-auto">{h.fmtEsperaFinal(v.tiempo_espera_seg)}</span>
        </div>
      )}
      <div className="flex items-start justify-between gap-2 mb-2">
        <div className="flex flex-col lg:flex-row lg:items-center lg:gap-2">
          <span className="font-display text-2xl font-black text-[#FFCC00] leading-none">{h.seqDeFolio(v.folio_impreso)}</span>
          <span className="text-[10px] font-mono tracking-tight text-[#FFCC00]/60 lg:text-white/50">{h.highlight(v.folio_impreso)}</span>
        </div>
        {cobraValet && (
          <span data-testid={`valet-pago-badge-${v.id_detalle}`}
            className={`shrink-0 text-[9px] font-black uppercase tracking-wider px-1.5 py-1 border leading-none ${h.esPagado(v) ? "bg-[#10B981]/15 text-[#10B981] border-[#10B981]/40" : "bg-[#EF4444]/15 text-[#EF4444] border-[#EF4444]/40"}`}>
            {h.esPagado(v) ? "Pagado" : "Por pagar"}
          </span>
        )}
      </div>
      <p className="text-sm font-semibold">{v.nombre_cliente ? h.highlight(v.nombre_cliente) : "—"}</p>
      <p className="text-xs text-white/50">{[v.marca_modelo, v.color].filter(Boolean).length ? h.highlight([v.marca_modelo, v.color].filter(Boolean).join(" · ")) : "Sin datos"}</p>
      {v.placa && <p className="text-xs font-mono text-white/70 mt-1">{h.highlight(v.placa)}</p>}
      {v.objetos_valor && <p className="text-[11px] text-[#F59E0B] mt-1">⚠ {v.objetos_valor}</p>}
      {v.confirmacion_cliente && (
        <p className="text-[11px] text-[#10B981] font-bold mt-1 flex items-center gap-1" data-testid={`valet-conforme-${v.id_detalle}`}>
          <ShieldCheck className="h-3.5 w-3.5" /> {v.confirmacion_origen === "Sistema" ? "Autofirma Sistema" : "Firma Cliente"}
        </p>
      )}
      <div className="flex flex-col gap-2 w-full mt-3">
        <div className="flex items-center w-full gap-1 lg:gap-2">
          <button onClick={() => h.setPhotoTarget(v)} data-testid={`valet-fotos-${v.id_detalle}`}
            className="relative h-11 w-11 lg:h-9 lg:w-auto lg:px-3 border-0 lg:border lg:border-white/20 lg:hover:border-[#FFCC00] text-[#FFCC00] lg:text-white text-xs font-bold uppercase flex items-center justify-center gap-1 transition-colors" title="Ver / capturar fotos">
            <Camera className="h-6 w-6 lg:h-3.5 lg:w-3.5" /> <span className="hidden lg:inline">Fotos</span>
            {Number(v.fotos_count) > 0 && (
              <span data-testid={`valet-fotos-count-${v.id_detalle}`}
                className="absolute -top-1.5 -right-1.5 min-w-[18px] h-[18px] px-1 rounded-full bg-[#FFCC00] text-[#0A0A0A] text-[10px] font-black flex items-center justify-center leading-none border border-[#0A0A0A]">
                {v.fotos_count}
              </span>
            )}
          </button>
          <button onClick={() => h.reimprimir(v)} data-testid={`valet-reimprimir-${v.id_detalle}`} title="Reimprimir boleto de valet"
            className="h-11 w-11 lg:h-9 lg:w-9 shrink-0 lg:ml-auto text-[#FFCC00] lg:text-white/35 hover:text-[#FFCC00] lg:hover:text-white/80 border border-transparent lg:hover:border-white/20 flex items-center justify-center transition-colors"><Printer className="h-6 w-6 lg:h-3.5 lg:w-3.5" /></button>
          <button onClick={() => { h.setNotaTarget(v); h.setNotaTexto(v.nota_incidencia || ""); }} data-testid={`valet-nota-${v.id_detalle}`} title="Nota de incidencia"
            className={`relative h-11 w-11 lg:h-9 lg:w-9 shrink-0 border flex items-center justify-center transition-colors ${v.nota_incidencia ? "text-[#38BDF8] border-[#38BDF8]/50 hover:bg-[#38BDF8]/10" : "text-white/40 border-white/15 hover:text-white/80 hover:border-white/30"}`}>
            <FileText className="h-6 w-6 lg:h-3.5 lg:w-3.5" />
            {v.nota_incidencia && <span className="absolute -top-1 -right-1 h-2.5 w-2.5 rounded-full bg-[#38BDF8] border border-[#0A0A0A]" />}
          </button>
          {estado === "Entregado" && Number(v.total_pagado) > 0 && (
            <button onClick={() => h.imprimirComprobantePago(h.pagoDesdeCard(v))} data-testid={`valet-reimprimir-recibo-${v.id_detalle}`} title="Reimprimir comprobante de pago"
              className="h-11 w-11 lg:h-9 lg:w-9 shrink-0 text-[#10B981] border border-[#10B981]/40 hover:border-[#10B981] hover:bg-[#10B981]/10 flex items-center justify-center transition-colors"><Receipt className="h-6 w-6 lg:h-3.5 lg:w-3.5" /></button>
          )}
        </div>
        {estado !== "Entregado" && (
          <div className="flex items-center w-full gap-1 lg:gap-2">
            {h.esPagado(v) ? (
              <button onClick={() => h.infoCobrado(v)} data-testid={`valet-cobro-${v.id_detalle}`} title="Cobrado (ver detalle)"
                className="h-11 lg:h-9 flex-1 bg-[#10B981]/15 border border-[#10B981]/50 text-[#10B981] hover:bg-[#10B981]/25 text-xs font-bold uppercase flex items-center justify-center gap-1.5 transition-colors"><DollarSign className="h-4 w-4" /> Pagado</button>
            ) : (
              <button onClick={() => h.openCobro(v, false)} data-testid={`valet-cobro-${v.id_detalle}`} title="Cobrar salida"
                className="h-11 lg:h-9 flex-1 bg-[#EF4444] border border-[#EF4444] text-white hover:bg-[#EF4444]/90 text-xs font-bold uppercase flex items-center justify-center gap-1.5 transition-colors"><DollarSign className="h-4 w-4" /> Cobrar</button>
            )}
            {!h.esPagado(v) && (
              <button onClick={() => h.openCobro(v, true)} data-testid={`valet-bp-${v.id_detalle}`} title="Boleto perdido (con penalización)"
                className="h-11 w-11 lg:h-9 lg:w-9 shrink-0 bg-[#F59E0B]/15 border border-[#F59E0B]/50 text-[#F59E0B] hover:bg-[#F59E0B]/25 flex items-center justify-center transition-colors"><TicketX className="h-5 w-5 lg:h-4 lg:w-4" /></button>
            )}
          </div>
        )}
        <div className="flex justify-between items-center w-full">
          {estado !== "En_Resguardo" ? (
            <button onClick={() => h.setRegresarTarget(v)} data-testid={`valet-regresar-${v.id_detalle}`}
              className="h-11 lg:h-9 px-3 max-lg:landscape:w-11 max-lg:landscape:px-0 max-lg:landscape:border max-lg:landscape:border-white/40 text-white/50 lg:text-white/35 max-lg:landscape:text-white/70 hover:text-white/80 lg:hover:text-white/70 text-[11px] font-bold uppercase flex items-center justify-center gap-1 transition-colors" title="Regresar al estado anterior">
              <ChevronLeft className="h-4 w-4 lg:h-3.5 lg:w-3.5" /> <span className="max-lg:landscape:hidden">Regresar</span>
            </button>
          ) : <span />}
          {estado !== "Entregado" && (
            <button onClick={() => h.avanzar(v)} data-testid={`valet-avanzar-${v.id_detalle}`}
              className="h-11 lg:h-9 px-4 max-lg:landscape:w-11 max-lg:landscape:px-0 max-lg:landscape:bg-transparent max-lg:landscape:border max-lg:landscape:border-[#FFCC00] bg-[#FFCC00] text-[#0A0A0A] max-lg:landscape:text-[#FFCC00] text-xs font-bold uppercase flex items-center justify-center gap-1" title="Avanzar al siguiente estado"><span className="max-lg:landscape:hidden">Avanzar</span> <ChevronRight className="h-4 w-4 lg:h-3.5 lg:w-3.5" /></button>
          )}
        </div>
      </div>
    </motion.div>
  );
}

// Redibuja si cambian los datos de la tarjeta (v), su columna, el modo cobro o la búsqueda.
// Además, SOLO las columnas con cronómetro en vivo (Solicitado=SLA, Pagado=permanencia) redibujan
// cada segundo (prop `now`), para que los contadores corran sin re-renderizar todo el tablero.
export const ValetVehicleCard = memo(ValetVehicleCardBase, (prev, next) => {
  if (prev.v !== next.v || prev.estado !== next.estado || prev.cobraValet !== next.cobraValet || prev.q !== next.q) return false;
  const timed = next.estado === "Solicitado" || next.estado === "Pagado";
  if (timed && prev.now !== next.now) return false;
  return true;
});
